import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar, ChevronDown, ChevronUp, Search } from "lucide-react";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tonalità coerenti con il resto della piattaforma (cyan/amber/violet)
const SIGLA_COLORS = {
  FE: "bg-amber-50 text-amber-800 border-amber-200",
  PR: "bg-violet-50 text-violet-800 border-violet-200",
  ML: "bg-rose-50 text-rose-800 border-rose-200",
  L104: "bg-indigo-50 text-indigo-800 border-indigo-200",
  RC: "bg-cyan-50 text-cyan-800 border-cyan-200",
  DS: "bg-emerald-50 text-emerald-800 border-emerald-200",
};

const fmt = (n) => {
  if (n === null || n === undefined) return "—";
  const num = Number(n);
  if (Number.isInteger(num)) return String(num);
  return num.toFixed(2).replace(/\.?0+$/, "");
};

export default function RiepilogoAssenzePanel({ defaultCollapsed = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const [anno, setAnno] = useState(new Date().getFullYear());
  const [search, setSearch] = useState("");

  useEffect(() => {
    let aborted = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/dashboard/riepilogo-assenze?anno=${anno}`);
        if (!aborted) setData(res.data);
      } catch {
        if (!aborted) setData(null);
      } finally {
        if (!aborted) setLoading(false);
      }
    };
    load();
    return () => { aborted = true; };
  }, [anno]);

  const labels = data?.labels || {};
  const sigle = useMemo(() => Object.keys(labels), [labels]);

  const filtered = useMemo(() => {
    const ops = data?.operatori || [];
    if (!search.trim()) return ops;
    const q = search.toLowerCase();
    return ops.filter(o =>
      (o.full_name || "").toLowerCase().includes(q) ||
      (o.figura_professionale || "").toLowerCase().includes(q) ||
      (o.asl_riferimento || "").toLowerCase().includes(q)
    );
  }, [data, search]);

  const totals = useMemo(() => {
    const ops = filtered;
    const init = sigle.reduce((acc, s) => ({ ...acc, [s]: { giorni: 0, ore: 0 } }), {});
    let totGiorni = 0, totOre = 0;
    ops.forEach(o => {
      sigle.forEach(s => {
        const b = o.breakdown?.[s] || { giorni: 0, ore: 0 };
        init[s].giorni += b.giorni;
        init[s].ore += b.ore;
      });
      totGiorni += o.totale?.giorni || 0;
      totOre += o.totale?.ore || 0;
    });
    return { perSigla: init, totale: { giorni: totGiorni, ore: Math.round(totOre * 100) / 100 } };
  }, [filtered, sigle]);

  const yearOptions = useMemo(() => {
    const cy = new Date().getFullYear();
    return [cy - 2, cy - 1, cy, cy + 1];
  }, []);

  return (
    <Card className="border border-cyan-100 shadow-sm" data-testid="riepilogo-assenze-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Calendar className="h-4 w-4 text-cyan-600" />
            Riepilogo Assenze {anno}
            <span className="text-xs font-normal text-gray-500">
              · {filtered.length} operator{filtered.length === 1 ? 'e' : 'i'}
            </span>
          </CardTitle>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cerca operatore..."
                className="h-8 text-xs pl-8 w-40 sm:w-56"
                data-testid="riepilogo-search"
              />
            </div>
            <select
              value={anno}
              onChange={(e) => setAnno(parseInt(e.target.value, 10))}
              className="h-8 px-2 text-xs rounded-md border border-gray-200 bg-white"
              data-testid="riepilogo-anno-select"
            >
              {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <button
              onClick={() => setCollapsed(c => !c)}
              className="h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600"
              title={collapsed ? "Espandi" : "Comprimi"}
              data-testid="riepilogo-toggle"
            >
              {collapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </CardHeader>

      {!collapsed && (
        <CardContent className="pt-0">
          {loading ? (
            <div className="py-6 text-center text-sm text-gray-500">Caricamento…</div>
          ) : !data || filtered.length === 0 ? (
            <div className="py-6 text-center text-sm text-gray-500">
              {search ? 'Nessun operatore corrisponde alla ricerca.' : 'Nessun operatore assegnato.'}
            </div>
          ) : (
            <div className="space-y-3">
              {/* Totale aggregato per il team */}
              <div className="bg-gradient-to-r from-cyan-50/50 to-amber-50/50 border border-cyan-100 rounded-lg p-3" data-testid="riepilogo-totale">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Totale team</div>
                  <div className="flex items-center gap-2 flex-wrap text-[11px]">
                    {sigle.map(s => {
                      const v = totals.perSigla[s];
                      if (!v.giorni && !v.ore) return null;
                      return (
                        <span key={s} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border ${SIGLA_COLORS[s]}`}>
                          <strong>{labels[s]}</strong>
                          <span className="tabular-nums">{fmt(v.giorni)}gg · {fmt(v.ore)}h</span>
                        </span>
                      );
                    })}
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-gray-900 text-white">
                      <strong>Totale</strong>
                      <span className="tabular-nums">{fmt(totals.totale.giorni)}gg · {fmt(totals.totale.ore)}h</span>
                    </span>
                  </div>
                </div>
              </div>

              {/* Tabella per operatore */}
              <div className="overflow-x-auto -mx-3 sm:mx-0">
                <table className="w-full text-xs min-w-[640px]">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wide text-gray-500 border-b border-gray-200">
                      <th className="py-2 px-3 font-semibold">Operatore</th>
                      {sigle.map(s => (
                        <th key={s} className="py-2 px-2 text-center font-semibold">
                          {labels[s]}
                        </th>
                      ))}
                      <th className="py-2 px-3 text-center font-semibold">Totale</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filtered.map(o => (
                      <tr key={o.id} className="hover:bg-gray-50/50" data-testid={`riepilogo-row-${o.id}`}>
                        <td className="py-2 px-3">
                          <div className="font-medium text-gray-900">{o.full_name}</div>
                          {(o.figura_professionale || o.asl_riferimento) && (
                            <div className="text-[10px] text-gray-500">
                              {o.figura_professionale}
                              {o.figura_professionale && o.asl_riferimento && ' · '}
                              {o.asl_riferimento && `ASL ${o.asl_riferimento.replace(/^asl\s+/i, '')}`}
                            </div>
                          )}
                        </td>
                        {sigle.map(s => {
                          const b = o.breakdown?.[s] || { giorni: 0, ore: 0 };
                          const empty = !b.giorni && !b.ore;
                          return (
                            <td key={s} className="py-2 px-2 text-center">
                              {empty ? (
                                <span className="text-gray-300">—</span>
                              ) : (
                                <span className={`inline-flex flex-col items-center px-2 py-0.5 rounded border ${SIGLA_COLORS[s]}`}>
                                  <span className="font-semibold tabular-nums leading-tight">{fmt(b.giorni)} gg</span>
                                  <span className="text-[10px] tabular-nums opacity-80 leading-tight">{fmt(b.ore)}h</span>
                                </span>
                              )}
                            </td>
                          );
                        })}
                        <td className="py-2 px-3 text-center">
                          <div className="font-semibold tabular-nums text-gray-900">{fmt(o.totale.giorni)} gg</div>
                          <div className="text-[10px] tabular-nums text-gray-500">{fmt(o.totale.ore)}h</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
